import React, { useMemo } from "react";

import {
  TrackingPipelineEvents,
  AnalyticsEvents,
} from "~/sdks/LocalContent/types";

import { useTracking } from "@local-logic/core/tracking";
import { useLocalContent } from "~/sdks/LocalContent/context";
import { useCommute } from "~/sdks/LocalContent/hooks/useCommute";

import type { POI, School } from "@local-logic/client";

import { DEFAULT_TRAVEL_MODE } from "~/sdks/LocalContent/constants";

import { TransitRoutes } from "~/sdks/LocalContent/components/TransitRoutes";
import { Header } from "./Header";
import { Address } from "./Address";
import { Link } from "./Link";
import { SchoolAttributes } from "./SchoolAttributes";
import { Commute } from "./Commute";

import * as styles from "./styles";

export function POIPopupContent() {
  const tracking = useTracking();
  const {
    commuteOptions,
    locale,
    location,
    distanceUnit,
    centerMarker,
    activeMarkerDetails,
    selectedPoiSubcategory,
    map,
    handleCalculateCommute,
  } = useLocalContent();
  const { data, isLoading } = useCommute({
    mapProvider: map?.mapProvider,
    mode: commuteOptions.commuteType,
    locale,
    units: distanceUnit,
    origin:
      location?.lat && location?.lng
        ? {
            lat: location.lat,
            lng: location.lng,
          }
        : undefined,
    destination:
      typeof centerMarker !== "undefined" &&
      activeMarkerDetails?.lat &&
      activeMarkerDetails?.lng
        ? {
            lat: activeMarkerDetails.lat,
            lng: activeMarkerDetails.lng,
          }
        : undefined,
  });

  const commuteTime = useMemo(() => {
    if (!data || isLoading) return undefined;

    return data.duration;
  }, [data?.mode, data?.duration, isLoading]);

  const handleOnViewRoute = () => {
    if (!activeMarkerDetails) {
      return;
    }

    const requestOptions = {
      commuteType: commuteOptions.commuteType,
      label: activeMarkerDetails?.address || activeMarkerDetails?.name,
      destination: {
        lat: activeMarkerDetails?.lat || 0,
        lng: activeMarkerDetails?.lng || 0,
      },
    };

    handleCalculateCommute(requestOptions);

    const eventAttributes: Record<string, boolean | string | number> = {
      label: requestOptions.label,
      mode: requestOptions.commuteType as string,
      destinationLat: requestOptions.destination.lat,
      destinationLng: requestOptions.destination.lng,
    };

    tracking.postEvent({
      eventType: TrackingPipelineEvents.MAP_POI_VIEW_ROUTE,
      eventAttributes: Object.entries(eventAttributes).map(([key, value]) => ({
        label: key,
        data: value,
      })),
    });

    tracking.trackAnalyticsEvent({
      eventName: AnalyticsEvents.MAP_POI_VIEW_ROUTE,
      eventAttributes,
    });
  };

  const handleLinkClick = () => {
    if (!selectedPoiSubcategory || !activeMarkerDetails) {
      return;
    }

    const eventAttributes: Record<string, boolean | string | number> = {
      category: selectedPoiSubcategory,
    };

    Object.entries(activeMarkerDetails).forEach(([key, value]) => {
      if (
        typeof value === "boolean" ||
        typeof value === "string" ||
        typeof value === "number"
      ) {
        eventAttributes[key] = value;
      }
    });

    tracking.postEvent({
      eventType: TrackingPipelineEvents.MAP_POI_POPUP_LINK_CLICKED,
      eventAttributes: Object.entries(eventAttributes).map(([key, value]) => ({
        label: key,
        data: value,
      })),
    });

    tracking.trackAnalyticsEvent({
      eventName: AnalyticsEvents.MAP_POI_POPUP_LINK_CLICKED,
      eventAttributes,
    });
  };

  if (!activeMarkerDetails) {
    return null;
  }

  return (
    <div className={styles.container}>
      <Header title={activeMarkerDetails.name} />

      <div className={styles.content}>
        <Address address={activeMarkerDetails.address} />
        <Link url={activeMarkerDetails.url} onClick={handleLinkClick} />

        <TransitRoutes
          keyPrefix="map-popup-transit-routes"
          routes={(activeMarkerDetails as POI).routes}
          useLongName={selectedPoiSubcategory === "train_stations"}
        />
      </div>

      <SchoolAttributes
        attributes={(activeMarkerDetails as School).school_attributes}
        boardAttributes={
          (activeMarkerDetails as School).school_board_attributes
        }
        onLinkClick={handleLinkClick}
      />

      {typeof centerMarker !== "undefined" && (
        <Commute
          isLoading={isLoading}
          commuteType={commuteOptions.commuteType || DEFAULT_TRAVEL_MODE}
          durationInSeconds={commuteTime}
          onCalculateCommute={handleOnViewRoute}
        />
      )}
    </div>
  );
}
